// File: src/components/CountryOptions.js
// The options inside a country <select>, grouped by continent.
//
// Eight selects across the app offer the same country list. They each used to
// map over it themselves, which was fine for twenty entries and is not for two
// hundred and fifty: the grouping, the per-language sorting and the option
// format would have had to be written eight times and kept in step by hand.
//
// THE NAME COMES FIRST AND THE FLAG AFTER, which looks backwards next to the
// old "flag then name" and is deliberate. A native select jumps to an option
// when you type the first letters of its label, and that is how anyone finds a
// country in a list this long. With the flag first, every option begins with
// the same kind of character and typing "it" for Italy matches nothing.

import React, { useMemo } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { groupedCountries } from '../utils/countryGroups';

/**
 * Country options for a <select>, as a fragment of <optgroup>s.
 *
 * `withFlag` is off where the caller draws its own flag - SeriesManagement
 * overlays a flag-icons span on the closed select, and a second flag inside the
 * list would be the same picture twice.
 */
function CountryOptions({ withFlag = true }) {
  const { t, language } = useLanguage();

  // Two hundred and fifty option elements, rebuilt on every render of every
  // form that holds a country field, is worth memoizing. The groups themselves
  // are already cached per language in countryGroups.
  return useMemo(() => {
    const groups = groupedCountries(language, t);

    return (
      <>
        {groups.map(group => (
          <optgroup key={group.continent} label={group.label}>
            {group.countries.map(country => (
              <option key={country.code} value={country.code}>
                {withFlag ? `${country.label} ${country.flag}` : country.label}
              </option>
            ))}
          </optgroup>
        ))}
      </>
    );
  }, [language, t, withFlag]);
}

export default CountryOptions;
